import { Component, inject, effect, Input, Output, EventEmitter, ChangeDetectionStrategy, OnChanges, SimpleChanges, OnInit, DestroyRef } from '@angular/core';
import Swal from 'sweetalert2';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModule, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AddPartyService } from './add-party.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { LOCALE_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@services/core/auth.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { PermissionService } from '@services/core/permission.service';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PartyFormComponent } from '../../components/party-form/party-form.component';
import { VehicleFormComponent } from '../../../management-vehicle/components/vehicle-form/vehicle-form.component';
import { DocumentGroupComponent } from '../../../management-document/document-group/document-group.component';
import { DocumentApprovalComponent } from '../../../management-document/components/document-approval/document-approval.component';
import { DocumentService } from '../../../management-document/services/document.service';
import { FormMode } from 'app/core/constants/form-mode';
import { forkJoin, of, Observable, from, firstValueFrom } from 'rxjs';
import { catchError, switchMap, filter } from 'rxjs/operators';
import { TimelineComponent } from 'app/core/components/timeline/timeline.component';
import { ImageOptimizerService } from 'app/core/service/utils/image-optimizer.service';
import { ViewerComponent } from '../../../management-document/components/viewer/viewer.component';
import { BpmService } from '../../../management-request/services/bpm.service';
import { RequestService } from '../../../management-request/services/requests.service';
import { Router } from '@angular/router';
import { StepApprovalPayload } from '../../../management-request/components/step-approval-modal/step-approval-modal.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { SocketService } from '@services/core/socket.service';
import { SheetSelectComponent } from 'app/core/components/sheet-select/sheet-select.component';
import { MasterDetailNav } from 'app/core/service/utils/master-detail-nav.service';


registerLocaleData(localeEs);

@Component({
  selector: 'app-add-party',
  templateUrl: './add-party.component.html',
  styleUrls: ['./add-party.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    NgbModule,
    NgSelectModule,
    ReactiveFormsModule,
    FormsModule,
    PartyFormComponent,
    VehicleFormComponent,
    DocumentGroupComponent,
    DocumentApprovalComponent,
    TimelineComponent,
    ViewerComponent,
    MobileFooterComponent,
    SheetSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AddPartyService, { provide: LOCALE_ID, useValue: 'es-ES' }]
})
export class AddPartyComponent implements OnInit, OnChanges {
  public Toast = Swal.mixin({
    toast: true, position: 'top-end'
    , showConfirmButton: false, timer: 2000, timerProgressBar: true
  })
  @Output() onClose = new EventEmitter<boolean>();
  @Output() headerEditorActionChange = new EventEmitter<'save' | 'finalize' | 'next' | null>();
  @Input() partyInput: any = null;
  @Input() vehicleInput: any = null;
  @Input() requestInput: any = null;
  @Input() mode: FormMode = FormMode.CREATE;
  FormMode = FormMode;
  private authService = inject(AuthService)
  private documentService = inject(DocumentService);
  private imageOptimizer = inject(ImageOptimizerService);
  public bpmService = inject(BpmService);
  private requestService = inject(RequestService);
  private router = inject(Router);
  public appSettings = inject(AppSettings);
  private socketService = inject(SocketService);
  private destroyRef = inject(DestroyRef);
  private md = inject(MasterDetailNav);
  /** Request DRAFT creada al guardar el socio (si no vino por @Input). */
  public createdRequest: any = null;
  public appHasAccess: string[] = [];
  public entity;
  public user;
  private readonly fieldNamesMap: Record<string, string> = {
    rut: 'RUT', names: 'Nombres', mail: 'Correo Electrónico', mobileNumber: 'Celular',
    partyId: 'ID de Socio', plate: 'Patente', brandCode: 'Marca', color: 'Color',
    modelCode: 'Modelo', year: 'Año', vin: 'Número de motor / Chasis (VIN)',
    km: 'Kilometraje', commercialValue: 'Valor Comercial', value: 'Valor Asegurado'
  };


  constructor(private addPartyService: AddPartyService,
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    public permissionService: PermissionService) {
    effect(() => {
      this.entity = this.authService.sharingEntity();
      this.user = this.authService.userData();
    })
  }

  /** Solicitud activa del wizard (input o creada al guardar socio). */
  get activeRequest(): any | null {
    return this.requestInput ?? this.createdRequest ?? this.party?.request ?? null;
  }

  get isDraftRequest(): boolean {
    return this.bpmService.isDraft(this.activeRequest);
  }

  get bpmBlockedReason(): string | null {
    if (!this.isDraftRequest) return null;
    if (!this.areAllDocumentsUploaded) {
      return 'Debe cargar todos los documentos del checklist (incluidos los complementarios) antes de enviar.';
    }
    return null;
  }

  /** Tras enviar (o en VIEW), se revisa con el mismo componente de siniestros. */
  get useDocumentApproval(): boolean {
    return this.mode === FormMode.VIEW || !this.isDraftRequest;
  }

  get canApproveDocuments(): boolean {
    return this.bpmService.canReviewContent(this.activeRequest, this.currentRole())
      || this.bpmService.canApprove(this.activeRequest, this.currentRole());
  }

  get canFinalApproveDocuments(): boolean {
    return this.bpmService.canApprove(this.activeRequest, this.currentRole());
  }

  get canReplaceObservedDocs(): boolean {
    return this.bpmService.canReplaceObservedDocuments(this.activeRequest, this.currentRole(), this.currentMail());
  }

  private currentRole(): string {
    const roleObj = this.authService.sharingRole() as any;
    return String(roleObj?.slug ?? roleObj?.name ?? 'VISUALIZADOR').toUpperCase();
  }

  private currentMail(): string | null {
    const identity = this.authService.sharingIdentity() as any;
    const data = this.authService.userData() as any;
    return String(identity?.mail ?? data?.mail ?? '').trim().toLowerCase() || null;
  }

  EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

  protected showSpinner = true
  protected showToast = false
  public isSaving = false;
  private today = new Date()
  protected dateMin
  public documents;
  /** Activos no obligatorios del snapshot disponibles para complementar. */
  public complementaryOptions: Array<{
    key: string;
    typeSlug: string;
    ownerSlug: string;
    categorySlug?: string | null;
    name: string;
  }> = [];
  public selectedComplementKey: string | null = null;
  documentsForm!: FormGroup;

  // Variables para el visor global de imágenes
  public isViewerOpen = false;
  public viewerItems: any[] = [];
  public activeViewerId: string | number = '';

  private _tab = 1;
  steps = [
    { id: 1, title: 'Información General' },
    { id: 2, title: 'Información del Vehículo' },
    { id: 3, title: 'Documentos y Fotos' }
  ];

  get tab(): number {
    return this._tab;
  }

  set tab(value: number) {
    this._tab = value;
    this.headerEditorActionChange.emit(this.headerEditorAction());
  }

  get stepTitle(): string {
    return this.steps.find((s) => s.id === this.tab)?.title ?? '';
  }

  headerEditorAction(): 'save' | 'finalize' | 'next' | null {
    if (this.mode === FormMode.VIEW) {
      return this.tab < 3 ? 'next' : null;
    }
    if (this.tab === 1 || this.tab === 2) return 'save';
    if (this.tab === 3 && !this.isDraftRequest) return 'finalize';
    return null;
  }

  canDeleteFromHeader(): boolean {
    return this.mode !== FormMode.VIEW && this.isDraftRequest && !!this.activeRequest;
  }

  canSubmitPartyFromFooter(): boolean {
    return this.appSettings.isMobile()
      && this.mode !== FormMode.VIEW
      && this.isDraftRequest
      && !!this.activeRequest
      && this.tab === 3
      && this.areAllDocumentsUploaded;
  }

  isEditorSaving(): boolean {
    return this.isSaving;
  }

  saveEditorFromHeader(): void {
    const action = this.headerEditorAction();
    if (action === 'next') {
      if (this.tab < 3) this.tab = this.tab + 1;
      return;
    }
    if (action === 'finalize') {
      this.changeTabShow(0);
      return;
    }
    if (this.tab === 1) this.submitParty(this.tab);
    if (this.tab === 2) this.submitVehicle(this.tab);
  }

  deleteFromHeader(): void {
    this.deleteRequestApproval();
  }

  public partyForm: FormGroup;
  public vehicleForm: FormGroup;
  public documentForm: FormGroup;
  public party;
  public vehicle;

  createForm() {
    this.createPartyForm();
    this.createVehicleForm();
  }

  private createPartyForm() {
    this.partyForm = this.formBuilder.group({
      rut: [null, Validators.required], partyTypeSlug: ['NATURAL', Validators.required],
      names: [null, Validators.required], birthday: [null],
      mail: [null, Validators.required], mobileNumber: [null, Validators.required],
      groupSlug: [null], entityCode: [null]
    });
  }

  // Inicializa el formulario reactivo del vehículo
  private createVehicleForm() {
    this.vehicleForm = this.formBuilder.group({
      partyId: [null], partyUuid: [null], plate: [null, Validators.required],
      brandCode: [null, Validators.required], color: [null, Validators.required],
      modelCode: [null, Validators.required], year: [null, Validators.required],
      vin: [null, Validators.required], km: [null, Validators.required],
      vehicleType: ['sedan', Validators.required], commercialValue: [null, Validators.required],
      value: [null, Validators.required], statusSlug: ['DRAFT', Validators.required],
      isTransmissionAutomatic: [false]
    });
  }

  ngOnInit() {
    this.createForm();
    this.initializeInputs();
    this.initializeDateLimit();
    this.showSpinner = false;
    queueMicrotask(() => this.headerEditorActionChange.emit(this.headerEditorAction()));
    this.bindCatalogDocRefresh();
  }

  /** Si sync aplicó a abiertas, recarga slots propios (request_document), no el catálogo vivo. */
  private bindCatalogDocRefresh(): void {
    this.socketService.onCatalogChanged().pipe(
      filter((ev) => this.shouldRefreshDocsFromCatalog(ev)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.refreshDocuments());
  }

  private shouldRefreshDocsFromCatalog(ev: { scopes?: string[]; applyToOpen?: boolean; processSlug?: string } | null): boolean {
    if (!ev || ev.applyToOpen !== true) return false;
    const scopes = (ev.scopes ?? []).map((s) => String(s).toLowerCase());
    const hit = !scopes.length
      || scopes.includes('all')
      || scopes.includes('documentrequirements')
      || scopes.includes('document-requirements');
    if (!hit) return false;
    const partyUuid = this.party?.uuid || this.partyInput?.uuid;
    if (!partyUuid) return false;
    const processSlug = this.activeRequest?.processSlug;
    if (ev.processSlug && processSlug && String(ev.processSlug) !== String(processSlug)) return false;
    return true;
  }

  /** Bandeja: party/vehicle pueden llegar tras el getByUuid o al cambiar de borrador. */
  ngOnChanges(changes: SimpleChanges) {
    if (!this.partyForm) return;
    if (changes['partyInput']?.currentValue) {
      const next = changes['partyInput'].currentValue;
      if (!this.party || this.party?.uuid !== next?.uuid) {
        this.createdRequest = null;
        this.documents = null;
        this.complementaryOptions = [];
        this.selectedComplementKey = null;
        this._tab = 1;
        this.setupPartyInput();
      } else if (!changes['partyInput'].firstChange) {
        this.setupPartyInput();
      }
    }
    if (changes['vehicleInput']) {
      const next = changes['vehicleInput'].currentValue;
      if (!next) {
        this.vehicle = null;
        this.vehicleForm?.reset({ vehicleType: 'sedan', statusSlug: 'DRAFT', isTransmissionAutomatic: false });
      } else if (!this.vehicle || this.vehicle?.uuid !== next?.uuid) {
        this.setupVehicleInput();
      }
    }
    if (changes['requestInput']?.currentValue) {
      this.createdRequest = null;
    }
  }

  private initializeInputs() {
    if (this.partyInput) {
      this.setupPartyInput();
    }
    if (this.vehicleInput) {
      this.setupVehicleInput();
    }
  }

  private setupPartyInput() {
    this.partyForm.patchValue(this.partyInput);
    this.party = this.partyInput;
    this.getDocumentView(this.partyInput.uuid);
    const id = this.partyInput.id ?? this.partyInput.partyId ?? this.partyInput.party_id;
    if (id) {
      this.vehicleForm.patchValue({ partyId: id });
    }
    if (this.partyInput.uuid) {
      this.vehicleForm.patchValue({ partyUuid: this.partyInput.uuid });
    }
  }

  // Formatea un número agregando separadores de miles para visualización
  private formatDisplayNumber(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    const clean = String(value).replace(/\./g, '').replace(',', '.').trim();
    const num = Number(clean);
    return isNaN(num) ? '' : num.toLocaleString('de-DE');
  }

  // Carga los datos del vehículo formateando los valores monetarios
  private setupVehicleInput() {
    const commercialValue = this.formatDisplayNumber(this.vehicleInput?.commercialValue);
    const value = this.formatDisplayNumber(this.vehicleInput?.value);
    this.vehicleForm.patchValue({ ...this.vehicleInput, commercialValue, value });
    this.vehicle = this.vehicleInput;
  }

  private initializeDateLimit() {
    const minDate = new Date(this.today);
    minDate.setDate(minDate.getDate() - 30);
    this.dateMin = minDate.toISOString().split('T')[0];
  }

  getDocumentView(uuid: string) {
    const params: any = { partyUuid: uuid };
    if (this.activeRequest?.processSlug) {
      params.processSlug = this.activeRequest.processSlug;
    }
    this.addPartyService.getDocumentView(params).subscribe({
      next: (res) => this.processDocumentView(res[0] ?? res),
      error: (err) => console.log(err)
    });
  }

  private processDocumentView(allDocs: any) {
    this.documents = this.toMembershipDocuments(allDocs);
    this.complementaryOptions = Array.isArray(allDocs?.complementaryOptions)
      ? allDocs.complementaryOptions
      : [];
    this.selectedComplementKey = null;
  }

  /** Requisitos de incorporación: socio + vehículo, sin evidencias de siniestro ni grupos extra. */
  private toMembershipDocuments(allDocs: any) {
    const documents = (allDocs?.categories ?? [])
      .flatMap((cat: any) => cat.documents ?? [])
      .filter((d: any) => this.isMembershipDocument(d))
      .map((d: any) => ({
        ...d,
        docTypeCode: d.docTypeCode ?? d.typeSlug,
        isRequired: d.isRequired !== false
      }));
    return {
      ...allDocs,
      categories: documents.length
        ? [{ docCategoryCode: 'MEMBERSHIP', docCategoryName: 'Documentos', documents }]
        : []
    };
  }

  private isMembershipDocument(doc: any): boolean {
    const origin = String(doc?.origin ?? '').toUpperCase();
    if (origin === 'CLAIM') return false;
    const process = String(doc?.processSlug ?? '').toUpperCase();
    return !process.includes('CLAIM') && !process.includes('SINIESTRO');
  }

  uploadDocumentFile(event: { uuid: string; file: File; origin?: string; expirationDate?: string | null; done?: (ok: boolean) => void }) {
    const origin = event.origin === 'VEHICLE' ? 'VEHICLE' : 'PARTY';
    this.documentService.uploadDocument(event.uuid, event.file, origin, {
      expirationDate: event.expirationDate || null
    }).subscribe({
      next: () => {
        this.Toast.fire({ icon: 'success', title: 'Subido con éxito' });
        event.done?.(true);
        this.refreshDocuments();
      },
      error: (e) => {
        this.Toast.fire({ icon: 'error', title: e.error?.message ?? 'No se pudo subir.' });
        event.done?.(false);
      }
    });
  }

  refreshDocuments() {
    const uuid = this.party?.uuid || this.partyInput?.uuid;
    if (uuid) {
      this.getDocumentView(uuid);
    }
  }

  openGlobalViewer(clickedId: string | number) {
    const docs = this.extractViewableDocuments();
    if (docs.length > 0) {
      this.viewerItems = docs;
      this.activeViewerId = clickedId;
      this.isViewerOpen = true;
    } else {
      this.showNoImagesAlert();
    }
  }

  private extractViewableDocuments(): any[] {
    const categories = this.documents?.categories ?? [];
    return categories.flatMap((cat: any) => 
      (cat.documents ?? []).filter((d: any) => this.isImageDocument(d) || this.isPdfDocument(d))
    ).map((d: any) => {
      const pdf = this.isPdfDocument(d);
      return {
        id: d.uuid ?? d.id,
        imageUrl: this.addPartyService.url + d.filePath,
        title: d.docTypeName ?? d.fileName,
        status: 'success',
        mimeType: d.mimeType ?? (pdf ? 'application/pdf' : null),
        fileName: d.fileName ?? null,
        kind: pdf ? 'pdf' as const : 'image' as const
      };
    });
  }

  private isPdfDocument(doc: any): boolean {
    if (!doc?.filePath) return false;
    const mime = String(doc.mimeType ?? '').toLowerCase();
    const name = String(doc.fileName ?? '').toLowerCase();
    const path = String(doc.filePath ?? '').toLowerCase();
    return mime.includes('pdf') || name.endsWith('.pdf') || path.endsWith('.pdf');
  }

  private isImageDocument(doc: any): boolean {
    if (!doc.filePath) return false;
    const isMimeImage = doc.mimeType?.toLowerCase().includes('image') ?? false;
    const isFileImage = !!doc.fileName?.match(/\.(jpeg|jpg|gif|png|webp)$/i);
    const isPathImage = !!doc.filePath?.match(/\.(jpeg|jpg|gif|png|webp)$/i);
    return isMimeImage || isFileImage || isPathImage;
  }

  private showNoImagesAlert() {
    Swal.fire({
      icon: 'info',
      title: 'Sin documentos',
      text: 'No hay documentos visualizables cargados.',
      confirmButtonText: 'Entendido'
    });
  }

  submitRequestApproval() {
    void this.sendDraftViaBpm();
  }

  /** Envío DRAFT → paso inicial BPM (mismo camino que bandeja vía flow-steps). */
  async sendDraftViaBpm(payload?: StepApprovalPayload): Promise<void> {
    const req = this.activeRequest;
    if (!req?.uuid || this.isSaving) return;
    const toStatusSlug = this.bpmService.submitTargetStatusSlug(req);
    if (!toStatusSlug) {
      Swal.fire('Error', 'El proceso no tiene paso inicial BPM configurado.', 'error');
      return;
    }
    if (!this.areAllDocumentsUploaded) {
      Swal.fire('Documentos pendientes', 'Debe cargar todos los documentos del checklist (incluidos los complementarios agregados).', 'warning');
      return;
    }

    this.isSaving = true;
    this.showSendingRequestAlert();
    try {
      await firstValueFrom(this.requestService.advanceFlowStep(req.uuid, {
        toStatusSlug,
        gloss: payload?.gloss ?? null,
        documentUuid: payload?.documentUuid ?? null,
        documentTypeName: payload?.documentTypeName ?? null,
        requiresDocument: payload?.requiresDocument ?? null
      }));
      this.handleRequestApprovalSuccess(req.uuid);
    } catch (err: any) {
      this.handleRequestApprovalError(err);
    } finally {
      this.isSaving = false;
    }
  }

  private showSendingRequestAlert() {
    Swal.fire({
      title: 'Enviando solicitud...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });
  }

  private handleRequestApprovalSuccess(requestUuid: string) {
    this.isSaving = false;
    Swal.close();
    Swal.fire({
      icon: 'success',
      title: 'Enviado con éxito',
      text: 'La solicitud de incorporación ingresó al flujo de aprobación.',
      timer: 2000,
      showConfirmButton: false
    }).then(() => {
      this.onClose.emit(true);
      if (requestUuid) {
        this.md.go(this.md.request(requestUuid));
      }
    });
  }

  private handleRequestApprovalError(err: any) {
    Swal.close();
    console.error(err);
    const msg = err?.error?.error ?? err?.error?.message ?? err?.message ?? 'No se pudo enviar la solicitud para aprobación.';
    Swal.fire('Error', msg, 'error');
    this.isSaving = false;
  }

  deleteRequestApproval() {
    const uuid = this.activeRequest?.uuid;
    if (!uuid) return;
    this.showConfirmDiscardAlert().then((result) => {
      if (result.isConfirmed) {
        this.executeDiscardRequest(uuid);
      }
    });
  }

  private showConfirmDiscardAlert() {
    return Swal.fire({
      title: '¿Desechar borrador?',
      text: 'La solicitud quedará como Desechada en el historial y podrá reactivarse después. No se borran los datos.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, desechar',
      cancelButtonText: 'Cancelar'
    });
  }

  private executeDiscardRequest(uuid: string) {
    this.isSaving = true;
    Swal.fire({
      title: 'Desechando borrador...',
      text: 'Por favor, espera',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });
    this.addPartyService.discardRequest(uuid).subscribe({
      next: () => this.handleDiscardSuccess(),
      error: (err) => this.handleDeleteError(err)
    });
  }

  private handleDiscardSuccess() {
    Swal.close();
    Swal.fire({
      icon: 'success',
      title: 'Borrador desechado',
      text: 'Quedó en el historial como Desechada. Puedes reactivarla si hace falta.',
      timer: 2200,
      showConfirmButton: false
    }).then(() => this.onClose.emit(true));
  }

  private handleDeleteError(err: any) {
    Swal.close();
    console.error(err);
    const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo desechar la solicitud.';
    Swal.fire('Error', msg, 'error');
    this.isSaving = false;
  }

  get areAllDocumentsUploaded(): boolean {
    if (!this.documents) return false;
    const docs = (this.documents?.categories ?? []).flatMap((cat: any) => cat.documents ?? []);
    // Obligatorios y opcionales ya agregados al checklist: ambos exigen archivo para enviar.
    if (docs.length === 0) return true;
    return docs.every((doc: any) => this.hasUploadedFile(doc));
  }

  get canComplementDocuments(): boolean {
    return this.mode !== FormMode.VIEW
      && !!this.activeRequest
      && this.complementaryOptions.length > 0;
  }

  addComplementaryDocument(): void {
    const key = this.selectedComplementKey;
    const req = this.activeRequest;
    if (!key || !req) return;
    const opt = this.complementaryOptions.find((o) => o.key === key);
    if (!opt) return;

    Swal.fire({ title: 'Agregando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.addPartyService.addDocumentFromSnapshot({
      requestUuid: req.uuid,
      typeSlug: opt.typeSlug,
      ownerSlug: opt.ownerSlug,
      vehicleId: this.vehicle?.id ?? this.vehicleInput?.id ?? undefined
    }).subscribe({
      next: () => {
        Swal.close();
        this.selectedComplementKey = null;
        this.Toast.fire({ icon: 'success', title: 'Documento agregado' });
        this.refreshDocuments();
      },
      error: (err) => {
        Swal.close();
        const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo agregar el documento';
        Swal.fire('Error', msg, 'error');
      }
    });
  }

  /** Quita un requisito complementario (no obligatorio) del checklist de la solicitud. */
  deleteComplementaryDocument(uuid: string): void {
    if (!uuid) return;
    const docs = (this.documents?.categories ?? []).flatMap((cat: any) => cat.documents ?? []);
    const doc = docs.find((d: any) => d.uuid === uuid || d.linkUuid === uuid);
    if (doc && doc.isRequired !== false) {
      this.Toast.fire({ icon: 'warning', title: 'Los obligatorios no se pueden eliminar' });
      return;
    }

    Swal.fire({ title: 'Eliminando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.addPartyService.deleteRequestDocument(uuid).subscribe({
      next: () => {
        Swal.close();
        this.Toast.fire({ icon: 'success', title: 'Requisito eliminado' });
        this.refreshDocuments();
      },
      error: (err) => {
        Swal.close();
        const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo eliminar el documento';
        Swal.fire('Error', msg, 'error');
      }
    });
  }

  private hasUploadedFile(doc: any): boolean {
    return !!(doc?.fileName || doc?.filePath || doc?.fileUuid);
  }



  private _showInvalidFormAlert(form: FormGroup, title: string) {
    const emptyFields: string[] = [];
    const invalidFormatFields: string[] = [];
    this.collectFormErrors(form, emptyFields, invalidFormatFields);
    
    const message = this.buildErrorMessage(emptyFields, invalidFormatFields);
    Swal.fire({
      icon: 'warning',
      title: `Formulario de ${title} Incompleto o Inválido`,
      text: message,
      confirmButtonText: 'Aceptar'
    });
  }

  private collectFormErrors(form: FormGroup, empty: string[], invalid: string[]) {
    Object.keys(form.controls).forEach((name) => {
      const control = form.controls[name];
      if (control.invalid) {
        this.classifyError(name, control, empty, invalid);
      }
    });
  }

  private classifyError(name: string, control: any, empty: string[], invalid: string[]) {
    const label = this.fieldNamesMap[name] ?? name;
    if (control.hasError('required')) {
      empty.push(label);
      return;
    }
    const isYearError = name === 'year' && (control.hasError('min') || control.hasError('max'));
    invalid.push(
      isYearError 
        ? `${label} (El año excede la antigüedad máxima permitida por reglas del sistema)`
        : `${label} (Valor inválido o fuera de rango)`
    );
  }

  private buildErrorMessage(empty: string[], invalid: string[]): string {
    let msg = '';
    if (empty.length > 0) {
      msg += `Por favor completa los siguientes campos requeridos: ${empty.join(', ')}.\n\n`;
    }
    if (invalid.length > 0) {
      msg += `Los siguientes campos tienen errores de validación: \n- ${invalid.join('\n- ')}`;
    }
    return msg;
  }

  submitParty(tab: number, destinationTab = tab + 1) {
    const partyUuid = this.party?.uuid ?? this.partyInput?.uuid;
    if (partyUuid && this.partyForm.pristine) {
      this.tab = destinationTab;
      return;
    }
    if (!this.partyForm?.valid) {
      this._showInvalidFormAlert(this.partyForm, 'Socio');
      return;
    }
    this.savePartyData(partyUuid, destinationTab);
  }

  private savePartyData(partyUuid: string | undefined, destinationTab: number) {
    const obs = partyUuid
      ? this.addPartyService.updateParty(partyUuid, { ...this.partyForm.value, uuid: partyUuid })
      : this.addPartyService.createParty(this.partyForm.value);

    obs.subscribe({
      next: (res: any) => this.handlePartySaveSuccess(res, destinationTab),
      error: (err) => this.handlePartySaveError(err, destinationTab)
    });
  }

  private handlePartySaveError(err: any, destinationTab: number) {
    const body = err?.error;
    if (err?.status === 409 && body?.code === 'ABANDONED_MEMBERSHIP' && body?.requestUuid) {
      this.promptReactivateAbandonedMembership(body, destinationTab);
      return;
    }
    console.error(err);
    const msg = body?.error ?? body?.message ?? err?.message ?? 'No se pudo guardar el socio.';
    Swal.fire('Error', msg, 'error');
  }

  private promptReactivateAbandonedMembership(body: any, destinationTab: number) {
    Swal.fire({
      icon: 'question',
      title: 'Solicitud abandonada',
      text: 'Este RUT tiene una solicitud de incorporación desechada. ¿Desea reactivarla?',
      showCancelButton: true,
      confirmButtonText: 'Sí, reactivar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (!result.isConfirmed) return;
      Swal.fire({ title: 'Reactivando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      this.addPartyService.reactivateRequest(body.requestUuid).subscribe({
        next: () => {
          const partyUuid = body.partyUuid;
          if (!partyUuid) {
            Swal.close();
            Swal.fire('Error', 'No se pudo identificar el socio reactivado.', 'error');
            return;
          }
          this.addPartyService.updateParty(partyUuid, { ...this.partyForm.value, uuid: partyUuid }).subscribe({
            next: (res: any) => {
              Swal.close();
              this.handlePartySaveSuccess(res, destinationTab);
              this.Toast.fire({ icon: 'success', title: 'Solicitud reactivada' });
            },
            error: (err) => {
              Swal.close();
              const msg = err?.error?.error ?? err?.error?.message ?? 'Reactivada, pero no se pudo actualizar el socio.';
              Swal.fire('Atención', msg, 'warning');
            }
          });
        },
        error: (err) => {
          Swal.close();
          const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo reactivar la solicitud.';
          Swal.fire('Error', msg, 'error');
        }
      });
    });
  }

  private handlePartySaveSuccess(res: any, destinationTab: number) {
    this.party = res;
    if (res?.request) {
      this.createdRequest = res.request;
    }
    this.getDocumentView(this.party.uuid);
    this.vehicleForm.patchValue({ partyId: this.party.id, partyUuid: this.party.uuid });
    this.tab = destinationTab;
  }

  // Sanitiza valores numéricos eliminando puntos de miles y comas
  private sanitizeNumericValue(value: unknown): number {
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    const raw = String(value ?? '').replace(/\./g, '').replace(',', '.').trim();
    const parsed = Number(raw);
    return isNaN(parsed) ? 0 : parsed;
  }

  // Construye payload de vehículo con datos numéricos limpios para la API
  private buildVehiclePayload(vehicleUuid?: string): Record<string, any> {
    const raw = this.vehicleForm.value;
    const partyId = this.party?.id ?? this.partyInput?.id ?? this.partyInput?.partyId ?? this.partyInput?.party_id ?? raw.partyId;
    const partyUuid = this.party?.uuid ?? this.partyInput?.uuid ?? raw.partyUuid;
    const value = this.sanitizeNumericValue(raw.value);
    const commercialValue = this.sanitizeNumericValue(raw.commercialValue);
    const km = this.sanitizeNumericValue(raw.km);
    const payload: Record<string, any> = {
      ...raw,
      partyUuid,
      value,
      commercialValue,
      km,
      ...(vehicleUuid ? { uuid: vehicleUuid } : {})
    };
    if (partyId != null && partyId !== '') payload.partyId = partyId;
    else delete payload.partyId;
    return payload;
  }

  // Procesa el envío del vehículo validando el formulario
  submitVehicle(tab: number, destinationTab = tab + 1) {
    const vehicleUuid = this.vehicle?.uuid ?? this.vehicleInput?.uuid;
    const partyUuid = this.party?.uuid ?? this.partyInput?.uuid;
    if (vehicleUuid && this.vehicleForm.pristine) return void (this.tab = destinationTab);
    this.prepareVehicleForm(partyUuid);
    if (!this.vehicleForm?.valid) return this._showInvalidFormAlert(this.vehicleForm, 'Vehículo');
    this.saveVehicleData(vehicleUuid, partyUuid, destinationTab);
  }

  // Asocia identificadores del socio al formulario del vehículo
  private prepareVehicleForm(partyUuid: string | undefined) {
    const partyId = this.party?.id ?? this.partyInput?.id ?? this.partyInput?.partyId ?? this.partyInput?.party_id;
    if (partyId) this.vehicleForm.patchValue({ partyId });
    if (partyUuid) this.vehicleForm.patchValue({ partyUuid });
  }

  // Guarda los datos del vehículo invocando el servicio correspondiente
  private saveVehicleData(vehicleUuid: string | undefined, partyUuid: string | undefined, tab: number) {
    const payload = this.buildVehiclePayload(vehicleUuid);
    const obs = vehicleUuid
      ? this.addPartyService.updateVehicle(vehicleUuid, payload)
      : this.addPartyService.createVehicle(payload);
    obs.subscribe({
      next: (res: any) => this.handleVehicleSaveSuccess(res, partyUuid, tab),
      error: (err) => this.handleVehicleSaveError(err, partyUuid, tab)
    });
  }

  private handleVehicleSaveError(err: any, partyUuid: string | undefined, destinationTab: number) {
    const body = err?.error;
    if (err?.status === 409 && body?.code === 'ABANDONED_VEHICLE' && body?.requestUuid) {
      this.promptReactivateAbandonedVehicle(body, partyUuid, destinationTab);
      return;
    }
    console.error(err);
    const msg = body?.error ?? body?.message ?? err?.message ?? 'No se pudo guardar el vehículo.';
    Swal.fire('Error', msg, 'error');
  }

  private promptReactivateAbandonedVehicle(body: any, partyUuid: string | undefined, destinationTab: number) {
    Swal.fire({
      icon: 'question',
      title: 'Solicitud abandonada',
      text: 'Esta patente tiene una solicitud de vehículo nuevo desechada. ¿Desea reactivarla?',
      showCancelButton: true,
      confirmButtonText: 'Sí, reactivar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (!result.isConfirmed) return;
      Swal.fire({ title: 'Reactivando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      this.addPartyService.reactivateRequest(body.requestUuid).subscribe({
        next: () => {
          const vehicleUuid = body.vehicleUuid;
          if (!vehicleUuid) {
            Swal.close();
            Swal.fire('Error', 'No se pudo identificar el vehículo reactivado.', 'error');
            return;
          }
          const payload = this.buildVehiclePayload(vehicleUuid);
          this.addPartyService.updateVehicle(vehicleUuid, payload).subscribe({
            next: (res: any) => {
              Swal.close();
              this.handleVehicleSaveSuccess(res, partyUuid || body.partyUuid, destinationTab);
              this.Toast.fire({ icon: 'success', title: 'Solicitud reactivada' });
            },
            error: (err) => {
              Swal.close();
              const msg = err?.error?.error ?? err?.error?.message ?? 'Reactivada, pero no se pudo actualizar el vehículo.';
              Swal.fire('Atención', msg, 'warning');
            }
          });
        },
        error: (err) => {
          Swal.close();
          const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo reactivar la solicitud.';
          Swal.fire('Error', msg, 'error');
        }
      });
    });
  }

  private handleVehicleSaveSuccess(res: any, partyUuid: string | undefined, destinationTab: number) {
    this.vehicle = res;
    if (partyUuid) {
      this.getDocumentView(partyUuid);
    }
    this.tab = destinationTab;
  }

  //refactorizar para bajar complejidad
  submitDocument(tab: number) {
    if (!this.documentsForm) return console.error('documentsForm es null o undefined');
    const controls = this._getControlsWithFile();
    if (controls.length === 0) {
      this.changeTabShow(0);
      return;
    }
    this.uploadDocumentsList(controls);
  }

  private uploadDocumentsList(controls: any[]) {
    this.isSaving = true;
    this._showLoadingAlert();
    const uploadObservables = controls.map((c) => this._uploadFile(c as FormGroup));
    forkJoin(uploadObservables).subscribe({
      next: (results) => this._handleUploadSuccess(results),
      error: (err) => this._handleUploadError(err)
    });
  }

  private _getControlsWithFile() {
    const documentsArray = this.documentsForm.get('documents') as import('@angular/forms').FormArray;
    return documentsArray.controls.filter(control => {
      const fg = control as FormGroup;
      return fg.get('file')?.value && fg.get('id')?.value;
    });
  }

  private _showLoadingAlert() {
    Swal.fire({
      title: 'Procesando...',
      text: 'Optimizando imágenes y subiendo documentos.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });
  }

  private _uploadFile(fg: FormGroup) {
    const file = fg.get('file')?.value;
    const id = fg.get('id')?.value ?? fg.get('uuid')?.value;
    const expirationDate = fg.get('expirationDate')?.value || null;
    if (fg.get('allowExpiration')?.value && !expirationDate) {
      return of({ error: true, message: 'Falta fecha de vencimiento' });
    }

    return from(this.imageOptimizer.optimize(file)).pipe(
      switchMap(optimized => this.addPartyService.uploadDocument(id, optimized || file, expirationDate)),
      catchError(err => {
        console.error('Upload Error', err);
        return of({ error: true });
      })
    );
  }

  private _handleUploadSuccess(results: any[]) {
    this.isSaving = false;
    const errors = results.filter(r => r && r.error).length;

    if (errors === 0) {
      this._showSuccessAlert();
    } else {
      this._showWarningAlert(errors);
    }
  }


  private _showSuccessAlert() {
    Swal.fire({
      icon: 'success',
      title: '¡Completado!',
      text: 'Todos los documentos se guardaron exitosamente.',
      timer: 2000,
      showConfirmButton: false
    }).then(() => this.changeTabShow(0));
  }

  private _showWarningAlert(errors: number) {
    Swal.fire({
      icon: 'warning',
      title: 'Finalizado con errores',
      text: `Hubo problemas al subir ${errors} documento(s).`,
    }).then(() => this.changeTabShow(0));
  }

  private _handleUploadError(err: any) {
    this.isSaving = false;
    console.error('Error forkJoin', err);
    Swal.fire({ icon: 'error', title: 'Error Crítico', text: 'Ocurrió un error inesperado.' });
  }



  changeTabShow(change) {
    this.onClose.emit(false)
  }

  onTimelineStepClick(stepId: number) {
    if (stepId === this.tab) return;
    if (this.mode === FormMode.VIEW || stepId < this.tab) {
      this.tab = stepId;
      return;
    }
    if (!this.activeRequest) return;
    if (this.tab === 1) this.submitParty(this.tab, stepId);
    if (this.tab === 2) this.submitVehicle(this.tab, stepId);
  }



}



